import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Bell,
  Check,
  ChevronDown,
  LogOut,
  Menu,
  Moon,
  Search,
  ShieldCheck,
  Sun,
} from 'lucide-react';
import { useLocation } from 'wouter';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { pageTitles } from '@/constants/navigation';
import { useAuth } from '@/contexts/auth-context';
import { useTheme } from '@/contexts/theme-context';
import { customerBillingService } from '@/services/customer-billing.service';

export function AppHeader({ onOpenMenu }: { onOpenMenu: () => void }) {
  const [location, navigate] = useLocation();
  const { user, logout } = useAuth();
  const { theme, resolvedTheme, setTheme } = useTheme();
  const queryClient = useQueryClient();
  const title = pageTitles[location] || 'ChefiaPro';
  const notifications = useQuery({
    queryKey: ['customer-notifications'],
    queryFn: customerBillingService.notifications,
    enabled: user?.role === 'OWNER',
    staleTime: 30_000,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });
  const markRead = useMutation({
    mutationFn: customerBillingService.markNotificationRead,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['customer-notifications'] }),
  });
  const unread = notifications.data?.filter((notification) => !notification.readAt).length ?? 0;

  return (
    <header className="sticky top-0 z-20 flex h-16 items-center gap-3 border-b bg-[rgb(var(--background))]/85 px-4 backdrop-blur-xl sm:px-6 lg:ml-64">
      <Button
        variant="ghost"
        size="icon"
        className="lg:hidden"
        onClick={onOpenMenu}
        aria-label="Abrir menu"
      >
        <Menu />
      </Button>
      <img
        src="/brand/chefiapro-mark.svg"
        alt=""
        aria-hidden="true"
        className="size-7 shrink-0 object-contain sm:hidden"
      />
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold">{title}</p>
        <p className="hidden text-xs text-[rgb(var(--muted-foreground))] sm:block">
          Visão consolidada da sua operação
        </p>
      </div>

      <div className="relative hidden w-full max-w-xs xl:block">
        <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-[rgb(var(--muted-foreground))]" />
        <Input
          className="h-9 bg-[rgb(var(--card))] pl-9"
          placeholder="Buscar no ChefiaPro..."
          aria-label="Buscar"
        />
      </div>

      {user?.role === 'OWNER' ? (
        <DropdownMenu.Root>
          <DropdownMenu.Trigger asChild>
            <Button variant="ghost" size="icon" aria-label="Notificações" className="relative">
              <Bell />
              {unread ? (
                <span className="absolute right-1.5 top-1.5 flex min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">
                  {unread > 9 ? '9+' : unread}
                </span>
              ) : null}
            </Button>
          </DropdownMenu.Trigger>
          <DropdownMenu.Portal>
            <DropdownMenu.Content
              align="end"
              sideOffset={8}
              className="z-50 max-h-[70vh] w-[min(24rem,calc(100vw-2rem))] overflow-y-auto rounded-xl border bg-[rgb(var(--card))] p-2 shadow-xl"
            >
              <div className="border-b px-2 py-2">
                <p className="font-semibold">Notificações</p>
                <p className="text-xs text-[rgb(var(--muted-foreground))]">
                  {unread ? `${unread} não lida(s)` : 'Nenhuma pendência'}
                </p>
              </div>
              {(notifications.data ?? []).slice(0, 10).map((notification) => (
                <DropdownMenu.Item
                  key={notification.id}
                  className={`mt-1 cursor-default rounded-lg p-3 text-sm outline-none hover:bg-[rgb(var(--muted))] ${notification.readAt ? 'opacity-70' : 'bg-brand-50/70 dark:bg-brand-950/30'}`}
                  onSelect={() => {
                    if (!notification.readAt) markRead.mutate(notification.id);
                    if (notification.actionUrl) navigate(notification.actionUrl);
                  }}
                >
                  <p className="font-semibold">{notification.title}</p>
                  <p className="mt-1 line-clamp-3 text-xs text-[rgb(var(--muted-foreground))]">
                    {notification.message}
                  </p>
                  <p className="mt-2 text-[10px] text-[rgb(var(--muted-foreground))]">
                    {new Intl.DateTimeFormat('pt-BR', {
                      dateStyle: 'short',
                      timeStyle: 'short',
                    }).format(new Date(notification.createdAt))}
                  </p>
                </DropdownMenu.Item>
              ))}
              {!notifications.isLoading && !notifications.data?.length ? (
                <p className="p-6 text-center text-sm text-[rgb(var(--muted-foreground))]">
                  Nenhuma notificação disponível.
                </p>
              ) : null}
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </DropdownMenu.Root>
      ) : null}

      <DropdownMenu.Root>
        <DropdownMenu.Trigger asChild>
          <Button variant="ghost" className="h-10 gap-2 px-2" aria-label="Menu do usuário">
            <span className="flex size-8 items-center justify-center rounded-lg bg-brand-100 text-xs font-bold text-brand-800 dark:bg-brand-900 dark:text-brand-200">
              {user?.name.slice(0, 2).toUpperCase()}
            </span>
            <span className="hidden max-w-32 truncate text-sm md:block">{user?.name}</span>
            <ChevronDown className="hidden size-3.5 md:block" />
          </Button>
        </DropdownMenu.Trigger>
        <DropdownMenu.Portal>
          <DropdownMenu.Content
            align="end"
            sideOffset={8}
            className="z-50 min-w-56 rounded-xl border bg-[rgb(var(--card))] p-1.5 shadow-xl"
          >
            <div className="border-b px-2 py-2.5">
              <p className="truncate text-sm font-medium">{user?.name}</p>
              <p className="truncate text-xs text-[rgb(var(--muted-foreground))]">{user?.email}</p>
            </div>
            <DropdownMenu.Item
              className="mt-1 flex cursor-default items-center gap-2 rounded-lg px-2 py-2 text-sm outline-none hover:bg-[rgb(var(--muted))]"
              onSelect={() => navigate('/seguranca')}
            >
              <ShieldCheck className="size-4 text-brand-600" /> Segurança e 2FA
            </DropdownMenu.Item>
            <DropdownMenu.Sub>
              <DropdownMenu.SubTrigger className="mt-1 flex cursor-default items-center gap-2 rounded-lg px-2 py-2 text-sm outline-none hover:bg-[rgb(var(--muted))]">
                {resolvedTheme === 'dark' ? (
                  <Moon className="size-4" />
                ) : (
                  <Sun className="size-4" />
                )}
                Aparência
              </DropdownMenu.SubTrigger>
              <DropdownMenu.Portal>
                <DropdownMenu.SubContent className="z-50 min-w-36 rounded-lg border bg-[rgb(var(--card))] p-1 shadow-xl">
                  {(['light', 'dark', 'system'] as const).map((option) => (
                    <DropdownMenu.Item
                      key={option}
                      className="flex cursor-default items-center justify-between rounded-md px-2 py-1.5 text-sm outline-none hover:bg-[rgb(var(--muted))]"
                      onSelect={() => setTheme(option)}
                    >
                      {{ light: 'Claro', dark: 'Escuro', system: 'Sistema' }[option]}
                      {theme === option ? <Check className="size-3.5" /> : null}
                    </DropdownMenu.Item>
                  ))}
                </DropdownMenu.SubContent>
              </DropdownMenu.Portal>
            </DropdownMenu.Sub>
            <DropdownMenu.Item
              className="flex cursor-default items-center gap-2 rounded-lg px-2 py-2 text-sm text-red-600 outline-none hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950"
              onSelect={() => void logout()}
            >
              <LogOut className="size-4" /> Sair
            </DropdownMenu.Item>
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>
    </header>
  );
}
